import { useState, useEffect } from 'react';
import { Play, Calendar, ChevronRight, Radio } from 'lucide-react';
import { ContentRail } from './ContentRail';
import { getTeamFlag } from '../../utils/teamFlags';
import { formatKickoff } from '../../utils/homeHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TodaysMatchesSection({ navigate }) {
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/match-feed`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        const today = [
          ...(data.starting_soon || []),
          ...(data.today || []),
        ].slice(0, 15);
        setMatches(today);
      })
      .catch(() => {});
  }, []);

  if (!matches.length) return null;

  return (
    <ContentRail
      icon={<Calendar className="w-5 h-5 text-sky-400 shrink-0" />}
      title="Today's Matches"
      subtitle="Scheduled for today"
      seeAllPath="/feed"
      navigate={navigate}
      testId="todays-matches-section"
    >
      {matches.map((m) => {
        const homeFlag = getTeamFlag(m.home);
        const awayFlag = getTeamFlag(m.away);
        const isSoon = m.match_status === 'starting_soon';
        const kickoff = m.time ? formatKickoff(m.start_time || m.time) : '';

        return (
          <div
            key={m.id || `${m.home}-${m.away}`}
            onClick={() => m.id ? navigate(`/matchday/${m.id}`) : null}
            className={`flex-shrink-0 w-[240px] sm:w-[260px] bg-[#141414] border rounded-xl p-4 cursor-pointer group snap-start transition-all duration-200 hover:bg-[#1a1a1a] ${
              isSoon ? 'border-amber-500/20 hover:border-amber-500/40' : 'border-white/[0.06] hover:border-sky-500/30'
            }`}
            data-testid={`today-match-${m.id || 'ext'}`}
          >
            {/* Status + kickoff */}
            <div className="flex items-center justify-between mb-3">
              {m.competition && (
                <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-wider truncate mr-2">{m.competition}</span>
              )}
              {isSoon ? (
                <span className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/15 rounded-full shrink-0">
                  <Radio className="w-2.5 h-2.5 text-amber-400" />
                  <span className="text-amber-400 text-[9px] font-bold">SOON</span>
                </span>
              ) : kickoff ? (
                <span className="text-white/40 text-[10px] font-medium shrink-0">{kickoff}</span>
              ) : null}
            </div>

            {/* Teams */}
            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                {homeFlag ? <span className="text-lg leading-none">{homeFlag}</span> : (
                  <span className="w-6 h-6 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-[8px] font-bold text-gray-300 shrink-0">{m.home.slice(0,3).toUpperCase()}</span>
                )}
                <span className="flex-1 text-white font-semibold text-sm truncate">{m.home}</span>
              </div>
              <div className="flex items-center gap-2.5">
                {awayFlag ? <span className="text-lg leading-none">{awayFlag}</span> : (
                  <span className="w-6 h-6 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-[8px] font-bold text-gray-300 shrink-0">{m.away.slice(0,3).toUpperCase()}</span>
                )}
                <span className="flex-1 text-white font-semibold text-sm truncate">{m.away}</span>
              </div>
            </div>

            {/* Followed badge */}
            {m.is_followed && (
              <div className="mt-2.5 flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full w-fit">
                <span className="text-emerald-400 text-[8px] font-bold">Your team</span>
              </div>
            )}

            {/* CTA */}
            <button
              onClick={(e) => { e.stopPropagation(); m.id ? navigate(`/matchday/${m.id}`) : navigate('/feed'); }}
              className="w-full mt-3 flex items-center justify-center gap-1.5 py-1.5 bg-white/[0.06] hover:bg-white/10 text-white/70 text-[10px] font-bold rounded-lg transition-colors uppercase tracking-wide border border-white/[0.06]"
              data-testid={`today-matchday-${m.id || 'ext'}`}
            >
              Match Day <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        );
      })}
    </ContentRail>
  );
}
